<template>
  <div class="offer-content-chain">
    <div ref="container" class="chain-container" @wheel.prevent="onWheel">
      <div class="chain-content">
        <template v-for="(payPackage, index) in sortedPackages">
          <div
            :id="'chain-tile-' + index"
            :key="'chain-tile-' + payPackage.id"
            class="chain-package"
            :class="[
              'state-' + packageState(payPackage),
              {
                'is-current': isCurrent(payPackage),
                // Every tile gets the hover-zoom (current/locked/collected).
                'is-clickable': true,
              },
            ]"
            @click="onTileClick(payPackage, index)"
          >
            <!-- Tile background container -->
            <div class="chain-tile" :style="tileStyle"></div>

            <!-- Package Assets -->
            <OfferPackageAssets
              :package="payPackage"
              :custom-style="assetsStyle"
            />

            <!-- Locked overlay -->
            <div
              v-if="packageState(payPackage) === 'locked'"
              :id="'chain-lock-' + index"
              class="chain-overlay chain-overlay-lock"
              :style="lockOverlayStyle"
            ></div>

            <!-- Collected overlay -->
            <div
              v-else-if="packageState(payPackage) === 'collected'"
              class="chain-overlay chain-overlay-collected"
              :style="collectedOverlayStyle"
            ></div>

            <button
              v-if="packageState(payPackage) !== 'collected'"
              :id="'chain-price-' + index"
              class="chain-buy-button"
              :class="{
                'is-locked': packageState(payPackage) === 'locked',
                'is-long-label': buttonLabel(payPackage).length > 9,
              }"
              :style="buttonStyle"
              @click.stop="onTileClick(payPackage, index)"
            >
              {{ buttonLabel(payPackage) }}
            </button>
            <div v-else class="chain-price-label chain-collected-label">
              {{ $t("events.winter.collect_button") }}
            </div>
            <div
              v-if="index === unlockingIndex"
              id="chain-unlock-lock"
              class="chain-overlay chain-overlay-lock"
              :style="lockOverlayStyle"
            ></div>
          </div>

          <!-- Connector arrow between tiles (skip after the last tile) -->
          <div
            v-if="arrowImage && index < sortedPackages.length - 1"
            :key="'chain-arrow-' + index"
            class="chain-arrow"
            :style="arrowStyle"
          ></div>
        </template>
      </div>
    </div>

    <div class="scroll-buttons">
      <div
        class="button-left pointer"
        :style="paginationLeftStyle"
        @click="scroll(-scrollSpeed)"
      ></div>
      <div
        class="button-right pointer"
        :style="paginationRightStyle"
        @click="scroll(scrollSpeed)"
      ></div>
    </div>
  </div>
</template>

<script>
import { offerMethodsMixin } from "../pay/offerMethodsMixin";
import OfferPackageAssets from "../pay/offer-package-assets.vue";
import { chainTileState } from "./mixins/chainTileState";
import { chainScroll } from "./mixins/chainScroll";
import { chainUnlockAnimation } from "./mixins/chainUnlockAnimation";

export default {
  name: "OfferLayoutChain",
  components: {
    OfferPackageAssets,
  },
  mixins: [
    offerMethodsMixin,
    chainTileState,
    chainScroll,
    chainUnlockAnimation,
  ],
  props: {
    // The chain offer (chain wrapper's `.offer`): packages + `.chain` assets.
    offer: {
      type: Object,
      required: true,
    },
    displayConfig: {
      type: Object,
      default: null,
    },
  },
  computed: {
    // Chain-specific asset config (tile/lock/collected images, duration).
    chainConfig() {
      return this.offer?.chain || {};
    },
    sortedPackages() {
      const packages = this.offer?.packages || [];
      return [...packages].sort(
        (a, b) =>
          (a?.pivot?.sort_order ?? a?.sort_order ?? 0) -
          (b?.pivot?.sort_order ?? b?.sort_order ?? 0)
      );
    },
    tileStyle() {
      const url = this.chainConfig.tile_image_path_full;
      return url ? { backgroundImage: `url("${url}")` } : {};
    },
    lockOverlayStyle() {
      const url = this.chainConfig.lock_image_path_full;
      return url ? { backgroundImage: `url("${url}")` } : {};
    },
    collectedOverlayStyle() {
      const url = this.chainConfig.collected_image_path_full;
      return url ? { backgroundImage: `url("${url}")` } : {};
    },
    arrowImage() {
      return this.chainConfig.arrow_image_path_full || "";
    },
    arrowStyle() {
      return this.arrowImage
        ? { backgroundImage: `url("${this.arrowImage}")` }
        : {};
    },
    // Scroll-button image from the chain config; null → CSS local green arrows.
    paginationImage() {
      return this.chainConfig.pagination_image_path_full || "";
    },
    // Same asset, mirrored to point left.
    paginationLeftStyle() {
      return this.paginationImage
        ? {
            backgroundImage: `url("${this.paginationImage}")`,
            transform: "scaleX(-1)",
          }
        : {};
    },
    // Asset used as-is on the right button.
    paginationRightStyle() {
      return this.paginationImage
        ? { backgroundImage: `url("${this.paginationImage}")` }
        : {};
    },
    assetsStyle() {
      return {
        top: "100px",
        bottom: "110px",
        left: "20px",
        right: "20px",
        width: "auto",
        justifyContent: "center",
      };
    },

    buttonStyle() {
      const style = (this.displayConfig && this.displayConfig.style) || {};
      const out = {};
      if (style["button-color"]) out["--btn-bg"] = style["button-color"];
      if (style["button-hover-color"])
        out["--btn-bg-hover"] = style["button-hover-color"];
      if (style["button-border-color"])
        out["--btn-border"] = style["button-border-color"];
      if (style["button-text-color"])
        out["--btn-text"] = style["button-text-color"];
      return out;
    },
  },
  watch: {
    sortedPackages() {
      this.runScrollAndMaybeUnlock();
    },
  },
  mounted() {
    this.runScrollAndMaybeUnlock();
  },
  beforeDestroy() {
    if (this.scrollAnimId) cancelAnimationFrame(this.scrollAnimId);
    this.gsap.killTweensOf("#chain-unlock-lock");
  },
  methods: {
    onTileClick(payPackage, index) {
      const state = this.packageState(payPackage);
      if (state === "current") {
        this.handlePurchase(payPackage);
      } else if (state === "locked") {
        this.$emit("locked");
        this.jiggleTile(index);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "../pay/offer-shared-styles";

// Tile width: 3.5 tiles fit the 1121px canvas. Strip padding reserves one leading
// slot (empty 1st position) and two trailing slots so first/last tiles can center.
$chain-tile-w: 267px;
$chain-lead-pad: 329px;
$chain-trail-pad: 525px;

// No task panel here (unlike season pass) — center the strip in the modal.
.offer-content-chain {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 420px;
  transform: translateY(-50%);
}

.chain-container {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  overflow-x: scroll;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  .chain-content {
    width: auto;
    height: 100%;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding-left: $chain-lead-pad;
    padding-right: $chain-trail-pad;
  }
}

// Connector arrow between consecutive tiles.
.chain-arrow {
  flex: 0 0 auto;
  align-self: center;
  width: 50px;
  height: 50px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
}

.chain-package {
  position: relative;
  flex: 0 0 auto;
  width: $chain-tile-w;
  height: 400px;

  .chain-tile {
    position: absolute;
    inset: 0;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
  }

  .chain-overlay {
    position: absolute;
    z-index: 6;
    background-repeat: no-repeat;
  }

  #chain-unlock-lock {
    z-index: 9;
  }

  // Lock icon + collected/purchased marker: small, bottom-left corner.
  .chain-overlay-lock,
  .chain-overlay-collected {
    bottom: 40px;
    width: 70px;
    height: 70px;
    background-size: contain;
    background-position: center;
  }
  .chain-overlay-lock {
    left: -5px;
  }
  .chain-overlay-collected {
    left: 0px;
  }

  // Whole-tile click affordance (current/buyable tile only).
  &.is-clickable {
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.02);
    }
  }

  // Price label where the CTA button used to be. Non-interactive so it never
  // intercepts the tile click.
  .chain-price-label {
    position: absolute;
    left: 50%;
    bottom: 38px;
    transform: translateX(-50%);
    z-index: 7;
    font-size: 18px;
    font-weight: bold;
    line-height: 1.1;
    color: #fff;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.7);
    pointer-events: none;
    white-space: nowrap;
  }
  .chain-collected-label {
    bottom: 65px;
    text-transform: uppercase;
  }

  // Buy button on the current tile — same position as the price label, but
  // interactive (the tile body no longer triggers the purchase).
  .chain-buy-button {
    position: absolute;
    left: 50%;
    bottom: 50px;
    transform: translateX(-50%);
    z-index: 8;
    pointer-events: auto;
    cursor: pointer;
    padding: 10px 26px;
    // Colors come from ACP display_config via inline CSS vars; fall back to the
    // project's standard green CTA (.ok-button-green.round-new) when unset.
    border: 2px solid var(--btn-border, #43ff00);
    border-radius: 50px;
    background: var(
      --btn-bg,
      linear-gradient(180deg, #2dac00 14%, $text-shadow-dark-green 100%)
    );
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.93), 0 0 3px rgba(0, 0, 0, 0.93),
      0 0 4px rgba(67, 255, 0, 0.43), 0 0 2px #43ff00, 0 0 2px #43ff00;
    font-family: Ubuntu-bold;
    font-size: 18px;
    font-weight: bold;
    line-height: 1.1;
    white-space: nowrap;
    text-transform: uppercase;
    color: var(--btn-text, #fff);
    text-shadow: 0 0 2px black;
    transition: transform 0.2s ease, background 0.2s ease, filter 0.2s ease;
    transform-origin: center center;

    &:hover {
      background: var(
        --btn-bg-hover,
        linear-gradient(180deg, $text-shadow-dark-green 14%, #2dac00 100%)
      );
    }

    &:active {
      transform: translateX(-50%) scale(0.95);
    }

    &:focus {
      outline: none;
    }

    &.is-long-label {
      padding: 5px 12px;
      font-size: 15px;
      letter-spacing: -0.2px;
    }
  }

  // Asset icon sizing within a tile
  ::v-deep .offer-assets {
    .asset-row-other {
      gap: 8px;
    }

    .asset-item-main {
      .asset-icon {
        width: 90px;
        height: 90px;
      }

      .asset-amount {
        font-size: 26px;
      }
    }

    .asset-item-other {
      .asset-icon {
        width: 46px;
        height: 46px;
      }

      .asset-amount {
        font-size: 14px;
      }
    }
  }
}

// Place arrows below the tile strip (no task panel to anchor against).
.scroll-buttons {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: 70px;
  z-index: 30;
  pointer-events: none;

  .pointer {
    pointer-events: auto;
    cursor: pointer;
    width: 58px;
    height: 58px;
    position: absolute;
    top: 20px;
  }

  .button-left {
    left: 24px;
    background: url(~@/assets/img/common/green_arrow_left.png) left/100% 100%
      no-repeat;
    &:hover {
      filter: $hover-shadow;
    }
  }

  .button-right {
    right: 24px;
    background: url(~@/assets/img/common/green_arrow_right.png) left/100% 100%
      no-repeat;
    &:hover {
      filter: $hover-shadow;
    }
  }
}
</style>
